import { esc } from '../shared/models/html-escape';

/**
 * VOYAGE RADAR'S POI-NEARBY POPUP — the list of hulls around a tasked point,
 * rendered into the reticle's own marker.
 *
 * Split out of `shared/vessel-popup.ts` in phase 8b, by the test the D7
 * amendment settled on: its only consumers are `poi-nearby.feature.ts` and
 * `services/nearby.service.ts`, both Voyage Radar's, and POI TASKING ITSELF is a
 * Voyage Radar capability — AISstream has no tasked point to be near. The
 * vessel CARD stayed shared, because that renders normalised vessel fields
 * every provider has and `popup.host.ts` is built around it.
 *
 * `esc` is imported rather than duplicated: HTML escaping is nobody's dialect.
 */

/**
 * How many nearby rows the POI popup renders at once. Higher than the
 * cluster's cap: this list IS the product of a POI refresh (there is no map
 * layer behind it), so it earns more height before it truncates.
 *
 * A cap alone would strand the tail — a 1000 km radius over a busy coast
 * returns hundreds — so the filter box searches the WHOLE list and renders its
 * matches through the same cap. Nothing is unreachable; only the default view
 * is bounded.
 */
const POI_POPUP_LIMIT = 60;

/** One /v1/vessels/nearby row — the ONLY fields that endpoint returns. */
export interface VoyagePoiNearbyVessel {
  mmsi: any;
  name?: any;
  shipType?: any;
  sog?: any;
  cog?: any;
  distanceKm?: any;
}

/** Per-render context the POI list needs from the controller. */
export interface VoyagePoiPopupContext {
  /** Cost of the per-vessel lookup a row click triggers. */
  lookupCredits: number;
  /** True when the hull is already drawn — its row flies for free instead. */
  onMap: (mmsi: string) => boolean;
  /** The radius the server actually served, for the subtitle. */
  radiusKm?: number | null;
  /**
   * The hull whose lookup is in flight, if any.
   *
   * Held in the CONTEXT rather than stamped onto the DOM by the click handler,
   * because the filter re-renders these rows from scratch on every keystroke —
   * a mark written straight to the clicked element would vanish the moment the
   * operator typed while waiting.
   */
  pendingMmsi?: string | null;
}

// ── Nearby-row field formatters ─────────────────────────────────────────────
//
// Exported, although only this file builds the POI popup that uses them today.
// The per-vessel nearby drawer renders the SAME six fields off the SAME
// endpoint, and every rule below is a statement about the FEED rather than
// about this popup: 102.3 kn and 360° are the AIS sentinels for "not
// reported", 'n/a'/'unknown'/'null' are the feed's ways of saying nothing, and
// ", all ships of this type" is an ITU suffix that is pure repetition in a
// list. A second surface re-deriving them is how one product ends up with two
// answers to "is 0 knots a speed" — and 0 knots is moored, which is a real
// answer, so the two would differ on a hull sitting at a berth.

/**
 * "180 m" · "3.4 km" · "27 km" — a nearby distance is a glance, not a fix.
 *
 * Metres below a kilometre because that band is where the list is densest: a
 * harbour returns a run of hulls that all print "0.2 km" at one decimal, and a
 * column of identical numbers cannot be the thing the list is sorted by.
 */
export function fmtDistanceKm(raw: any): string | null {
  const n = Number(raw);
  if (raw == null || raw === '' || !Number.isFinite(n)) return null;
  if (n < 1) return `${Math.round(n * 1000)} m`;
  return n < 10 ? `${n.toFixed(1)} km` : `${Math.round(n)} km`;
}

/**
 * Strings the AIS feed uses for "the vessel did not say", which must render as
 * nothing rather than as a value. `row()` above already drops 'N/A'; this is
 * the same rule for the list's own fields.
 */
const POI_ABSENT = new Set(['', 'n/a', 'not available', 'unknown', 'undefined', 'null']);

export function poiText(raw: any): string | null {
  const s = String(raw ?? '').trim();
  return s && !POI_ABSENT.has(s.toLowerCase()) ? s : null;
}

/**
 * "Cargo, all ships of this type" → "Cargo".
 *
 * The operator sends the ITU category verbatim, and that suffix is on most of
 * them — so in a list it is pure repetition that pushes the part that differs
 * off the end of a 300px card. Only the boilerplate is removed; any type the
 * feed spells differently is left exactly as it came.
 */
export function poiShipType(raw: any): string | null {
  const s = poiText(raw);
  if (!s) return null;
  return s.replace(/,\s*all ships of this type$/i, '').trim() || null;
}

/**
 * Course over ground, or null when the vessel is not reporting one.
 *
 * 360° is the AIS sentinel for "course not available" — printing it as a
 * heading claims the vessel is pointing due north, which is a different
 * statement from silence. Values are normalised into [0, 360).
 */
export function poiCog(raw: any): number | null {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n >= 360 || n < 0) return null;
  return Math.round(n) % 360;
}

/**
 * Speed over ground in knots, or null when unreported. 102.3 is the AIS
 * sentinel for "speed not available"; 0 is a real, meaningful value (moored),
 * so it must survive.
 */
export function poiSog(raw: any): number | null {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0 || n >= 102.3) return null;
  return n;
}

/** One rendered row of the nearby list. */
function poiRow(v: VoyagePoiNearbyVessel, ctx: VoyagePoiPopupContext): string {
  const mmsi = String(v?.mmsi ?? '').trim();
  const name = poiText(v?.name);
  // A silent hull is still a hull: its MMSI becomes the heading, and the
  // sub-line then omits the duplicate.
  const heading = esc(name || mmsi || 'Unknown');
  const dist = fmtDistanceKm(v?.distanceKm);
  const onMap = !!mmsi && ctx.onMap(mmsi);

  const sog = poiSog(v?.sog);
  const cog = poiCog(v?.cog);
  const sub = [
    // MMSI leads the sub-line: a row click BUYS this vessel's details, and the
    // MMSI is the identity being bought — a name alone is not unique.
    name && mmsi ? esc(mmsi) : null,
    poiShipType(v?.shipType) ? esc(poiShipType(v?.shipType)) : null,
    sog != null ? `${esc(sog)} kn` : null,
    cog != null ? `COG ${esc(cog)}°` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  // A click on this row starts an opaque network round trip that ends with the
  // map flying somewhere else entirely. Without a mark ON THE ROW, the only
  // evidence the click registered is the result — so the operator's reading of
  // a slow request is that nothing happened, and the second click is refused
  // in silence by the one-lookup-at-a-time guard.
  const busy = !!mmsi && String(ctx.pendingMmsi ?? '').trim() === mmsi;

  const badge = busy
    ? `<span class="ais-popup__poispin" aria-hidden="true"></span>`
    : onMap
      ? `<span class="ais-popup__poionmap">On map</span>`
      : '';
  const title = busy
    ? 'Fetching this vessel’s latest details…'
    : onMap
      ? 'Already on the map — go to it'
      : `Fetch latest details and place on the map · ${ctx.lookupCredits} ` +
        `${ctx.lookupCredits === 1 ? 'credit' : 'credits'}`;
  // The row's own data stays on screen — it is what the operator just chose by
  // — so the sub-line is the one line that changes, to say the click landed.
  const subLine = busy ? 'Fetching latest position and details…' : sub;

  return (
    // `data-card-action` is what the shared host dispatches on since D15; the
    // class stays because `styles.scss` paints the row with it.
    `<button type="button" data-card-action="poirow" `
    + `class="ais-popup__poirow${onMap ? ' is-onmap' : ''}` +
      `${busy ? ' is-busy' : ''}" data-mmsi="${esc(mmsi)}" title="${title}"` +
      `${busy ? ' aria-busy="true"' : ''}>` +
      `<span class="ais-popup__poimain">` +
        `<span class="ais-popup__poiname">${heading}</span>${badge}` +
        (dist ? `<span class="ais-popup__poidist">${esc(dist)}</span>` : '') +
      `</span>` +
      (subLine ? `<span class="ais-popup__poisub">${subLine}</span>` : '') +
    `</button>`
  );
}

/**
 * The scrolling body of the POI list — count line, rows, overflow note.
 *
 * Exported because the filter box re-renders exactly this, in place: the
 * controller swaps the container's innerHTML on every keystroke rather than
 * rebuilding the popup, so the input keeps focus and its caret.
 *
 * `query` matches name OR MMSI against the FULL list, which is what makes the
 * hulls past the cap reachable at all.
 */
export function buildVoyagePoiNearbyRows(
  vessels: VoyagePoiNearbyVessel[],
  ctx: VoyagePoiPopupContext,
  query = '',
): string {
  const all = Array.isArray(vessels) ? vessels : [];
  const q = String(query ?? '').trim().toLowerCase();
  const matches = q
    ? all.filter((v) => {
        const name = String(v?.name ?? '').toLowerCase();
        const mmsi = String(v?.mmsi ?? '').toLowerCase();
        return name.includes(q) || mmsi.includes(q);
      })
    : all;

  if (!matches.length) {
    const msg = q
      ? `No vessel here matches “${esc(query.trim())}”`
      : 'No vessels reported in range';
    return `<div class="ais-popup__poiempty">${msg}</div>`;
  }

  const shown = matches.slice(0, POI_POPUP_LIMIT);
  const rest = matches.length - shown.length;
  // Says what the list IS showing, not just what exists — the old copy put
  // "+140 more" at the foot of a scroller, where it was never read.
  const meta = q
    ? `${matches.length} ${matches.length === 1 ? 'match' : 'matches'}` +
      (rest > 0 ? ` · nearest ${shown.length} shown` : '')
    : rest > 0
      ? `Nearest ${shown.length} of ${all.length} — filter to reach the rest`
      : `${all.length} ${all.length === 1 ? 'vessel' : 'vessels'}, nearest first`;

  return (
    `<div class="ais-popup__poimeta">${meta}</div>` +
    shown.map((v) => poiRow(v, ctx)).join('')
  );
}

/**
 * The POI target's popup — the list of vessels near the tasked point.
 *
 * A POI refresh (/v1/vessels/nearby) returns NO positions, so this list is the
 * whole result: nothing is drawn until a row is clicked, which buys that one
 * vessel's full details (position included) via the by-MMSI lookup and places
 * it like a search result. Rows already on the map are badged and fly for free.
 *
 * Rows are real <button>s — keyboard-reachable, and the same click-delegate
 * pattern as the card's header actions (`data-mmsi` + a class, wired in
 * `ensurePopupActionsWired`; Leaflet popups render outside Angular).
 */
export function buildVoyagePoiNearbyPopup(
  vessels: VoyagePoiNearbyVessel[],
  ctx: VoyagePoiPopupContext,
): string {
  const list = Array.isArray(vessels) ? vessels : [];
  const total = list.length;
  const radius = Number(ctx.radiusKm);
  const within =
    Number.isFinite(radius) && radius > 0
      ? ` within ${esc(Math.round(radius))} km`
      : '';

  // Offered once the list is long enough to be worth searching — below that it
  // is a control that costs height and saves nothing.
  const filter =
    total > 12
      ? `<input type="search" class="ais-popup__poifilter" ` +
        `placeholder="Filter by name or MMSI" aria-label="Filter nearby vessels">`
      : '';

  // Priced on the FULL list, not the rendered page: the answer to "does
  // clicking cost me anything" must not change as the user scrolls or filters.
  const anythingToBuy = list.some((v) => {
    const m = String(v?.mmsi ?? '').trim();
    return m && !ctx.onMap(m);
  });
  // A footer, and deliberately not a trailing row: at the bottom of a
  // 60-row scroller the price was below the fold on every screen that
  // mattered, which meant the first click was always the one made without it.
  const foot = anythingToBuy
    ? `<div class="ais-popup__poifoot">Click a vessel for its latest details and position — ` +
      `${esc(ctx.lookupCredits)} ${ctx.lookupCredits === 1 ? 'credit' : 'credits'} each</div>`
    : '';

  // On the ROOT as well as on the row: one lookup runs at a time, so while one
  // is in flight every OTHER row is an offer that would be refused. The class
  // is what stands them down (see `styles.scss`) — and it is baked into the
  // markup, not only toggled by the controller, so a card reopened mid-request
  // comes back in the same state it was dismissed in.
  const busy = !!String(ctx.pendingMmsi ?? '').trim();

  return (
    `<div class="ais-popup ais-popup--poi${busy ? ' is-busy' : ''}">` +
      `<div class="ais-popup__poihead">` +
        `<div class="ais-popup__head">` +
          `<span class="ais-popup__name">${total} ${total === 1 ? 'vessel' : 'vessels'} nearby${within}</span>` +
          `<span class="ais-popup__acts">` +
            `<button type="button" class="ais-popup__act ais-popup__close" ` +
            `title="Close" aria-label="Close">` +
            `<span class="material-icons-outlined">close</span></button>` +
          `</span>` +
        `</div>` +
        filter +
      `</div>` +
      `<div class="ais-popup__poirows">${buildVoyagePoiNearbyRows(list, ctx)}</div>` +
      foot +
    `</div>`
  );
}
